import { useQuery } from "@tanstack/react-query";
import { useMemo } from "react";
import { listManufacturers, listBrandLogos } from "@/lib/products.functions";
import { greeklish } from "@/lib/format";

/**
 * Returns a Map of manufacturer ps_id -> logo_url for active brands.
 * Used to show brand logos in filter lists where only the brand id/name is
 * available from product facets.
 */
export function useBrandLogos() {
  const { data } = useQuery({
    queryKey: ["manufacturers", "logos"],
    queryFn: () => listManufacturers(),
    staleTime: 10 * 60_000,
  });
  const map = new Map<number, string | null>();
  for (const m of data?.manufacturers ?? []) map.set(m.ps_id, m.logo_url);
  return map;
}

export type BrandBadge = { ps_id: number; name: string; logo_url: string; slug: string };

/**
 * Λεπτός κατάλογος μαρκών που έχουν logo (ps_id -> logo + slug σελίδας μάρκας).
 * Χρησιμοποιείται για το σήμα μάρκας πάνω στις κάρτες προϊόντων.
 */
export function useBrandBadges() {
  const { data } = useQuery({
    queryKey: ["brands", "badges"],
    queryFn: () => listBrandLogos(),
    staleTime: 30 * 60_000,
  });
  return useMemo(() => {
    const map = new Map<number, BrandBadge>();
    for (const b of data?.brands ?? []) {
      if (!b.logo_url) continue;
      map.set(b.ps_id, {
        ps_id: b.ps_id,
        name: b.name,
        logo_url: b.logo_url,
        slug: `${b.ps_id}-${greeklish(b.name)}`,
      });
    }
    return map;
  }, [data]);
}